import type { CalendarEvent, NotificationPreferences } from '@/api/types';
import { ACCOUNT_PARAM } from '@/app/accountSearchParam';
import {
  addDayKey,
  dayKeyInZone,
  fmtTimeIn,
  fmtWeekdayLongOf,
  startOfZonedDay,
  zonedParts,
} from '@/features/calendar/zone';
import { allDayKeyOf } from '@/routes/calendar/datetime';

/**
 * Calendar reminders, the pure half: which reminders are due when, what their
 * notice says, and where its click goes. The hook that turns this into timers
 * and notices is `useCalendarReminders`.
 *
 * Two parties raise these notices. The server sends a Web Push at each
 * reminder's trigger time for an owner with background notifications on
 * (backend internal/httpapi/notify_reminders.go); the open app raises its own
 * as a fallback for everyone else — no background notifications, the desktop
 * app (which has no Web Push), a browser that never registered. Both must name
 * the same occurrence the same way, which is what `reminderTag` and
 * `reminderUrl` are for: a change to either is a change to the Go side too.
 */

/** Deep-link parameters the calendar reads (routes/calendar/CalendarView.tsx). */
export const EVENT_PARAM = 'event';
export const AT_PARAM = 'at';

/** Only triggers this far ahead get a timer; the rest wait for a later plan. */
export const SCHEDULE_HORIZON_MS = 24 * 60 * 60_000;
/**
 * How late a reminder may still be raised. A tab opened (or a laptop woken)
 * five minutes after "10 minutes before" should still say the meeting is
 * coming; one opened an hour after "1 day before" should not dig it up.
 */
export const STALE_AFTER_MS = 15 * 60_000;
/** The longest delay `setTimeout` honours; anything longer fires at once. */
export const MAX_TIMER_MS = 2 ** 31 - 1;
/**
 * A notice already on screen under the same tag counts as this reminder only
 * if it is this recent — see `REMINDER_DUPLICATE_MS` in public/push-sw.js,
 * which has to agree.
 */
export const REMINDER_DUPLICATE_MS = 2 * 60_000;

/**
 * The notification tag for one occurrence of an event.
 *
 * `eventId` is `CalendarEvent.id`, the resource id, which every occurrence of a
 * series shares; the start instant tells occurrences apart. MUST match
 * `reminderTag` in backend/internal/httpapi/notify_reminders.go, or the push
 * and the open app's fallback raise two notices for one reminder.
 */
export function reminderTag(eventId: string, startIso: string): string {
  return `ols-reminder-${eventId}-${Math.floor(Date.parse(startIso) / 1000)}`;
}

/**
 * Where a reminder's click lands: the day view, with that occurrence open.
 * `at` is the occurrence start in UTC (`2026-09-22T14:00:00Z`), not the
 * server's string verbatim, so the two sides build byte-identical URLs.
 */
export function reminderUrl(eventId: string, startIso: string, accountId: string): string {
  const at = new Date(Date.parse(startIso)).toISOString().replace(/\.000Z$/, 'Z');
  return (
    `/calendar/day?${EVENT_PARAM}=${encodeURIComponent(eventId)}` +
    `&${AT_PARAM}=${encodeURIComponent(at)}` +
    `&${ACCOUNT_PARAM}=${encodeURIComponent(accountId)}`
  );
}

/**
 * When the event actually begins for the viewer.
 *
 * A timed event's start is its instant. An all-day event's `start` is a
 * midnight-UTC carrier for a DATE (see `allDayKeyOf`), which west of Greenwich
 * is the evening before — so "never remind after it started" and "1 day
 * before" are measured from local midnight of the day it is on instead.
 */
export function effectiveStart(event: CalendarEvent, zone: string): number {
  if (!event.allDay) return Date.parse(event.start);
  return startOfZonedDay(allDayKeyOf(new Date(event.start)), zone).getTime();
}

/** "Starts at 2:30 PM", "Tomorrow at 9:00 AM", "All day" — plus the place. */
export function reminderBody(event: CalendarEvent, now: Date, zone: string): string {
  const today = dayKeyInZone(now, zone);
  const tomorrow = addDayKey(today, 1);
  let when: string;
  if (event.allDay) {
    const day = allDayKeyOf(new Date(event.start));
    when =
      day === today
        ? 'All day'
        : day === tomorrow
          ? 'Tomorrow, all day'
          : `${fmtWeekdayLongOf(day, zone)}, all day`;
  } else {
    const start = new Date(event.start);
    const day = dayKeyInZone(start, zone);
    const time = fmtTimeIn(start, zone);
    when =
      day === today
        ? `Starts at ${time}`
        : day === tomorrow
          ? `Tomorrow at ${time}`
          : `${fmtWeekdayLongOf(day, zone)} at ${time}`;
  }
  const place = event.location?.trim();
  return place ? `${when} · ${place}` : when;
}

export interface PlannedReminder {
  /** Tag + trigger: one occurrence can carry several reminders. */
  key: string;
  tag: string;
  triggerAt: number;
  startsAt: number;
  title: string;
  event: CalendarEvent;
}

/**
 * Every reminder that should be raised from now on, soonest first.
 *
 * Only reminders the event itself carries: an event without any gets no
 * notice, because inventing a default would be a setting nobody chose.
 * `email` reminders are the server's to send (it is the only one that can),
 * and the method is compared without case — the server writes VALARM's
 * `DISPLAY`, the client type says `display`.
 *
 * Kept: triggers up to `SCHEDULE_HORIZON_MS` ahead, and past ones no older
 * than `STALE_AFTER_MS` whose event has not begun. A cancelled event has
 * nothing to remind anyone of.
 */
export function planReminders(
  events: readonly CalendarEvent[],
  now: number,
  zone: string,
): PlannedReminder[] {
  const out: PlannedReminder[] = [];
  const seen = new Set<string>();
  for (const event of events) {
    if (event.status === 'cancelled') continue;
    const startsAt = effectiveStart(event, zone);
    if (!Number.isFinite(startsAt) || startsAt <= now) continue;
    const tag = reminderTag(event.id, event.start);
    for (const reminder of event.reminders ?? []) {
      if (String(reminder.method).toLowerCase() === 'email') continue;
      const minutes = Number(reminder.minutesBefore);
      if (!Number.isFinite(minutes) || minutes < 0) continue;
      const triggerAt = startsAt - minutes * 60_000;
      if (triggerAt > now + SCHEDULE_HORIZON_MS) continue;
      if (triggerAt < now - STALE_AFTER_MS) continue;
      const key = `${tag}@${triggerAt}`;
      // The same occurrence listed twice (it sits in two cached ranges, or a
      // duplicate VALARM) is still one reminder.
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, tag, triggerAt, startsAt, title: event.title.trim() || '(No title)', event });
    }
  }
  return out.sort((a, b) => a.triggerAt - b.triggerAt);
}

/** Minutes past midnight for an "HH:mm" value, or undefined for garbage. */
function minutesOf(value: string): number | undefined {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;
  return hours * 60 + minutes;
}

/**
 * Whether `now` falls inside the owner's quiet hours, read on the wall clock
 * of the zone they were set in. The window may wrap midnight (22:00–07:00).
 * A malformed or empty window is not quiet: silencing a reminder needs the
 * owner to have actually asked for it.
 */
export function inQuietHours(
  quiet: NotificationPreferences['quietHours'],
  now: Date,
  fallbackZone: string,
): boolean {
  if (!quiet) return false;
  const start = minutesOf(quiet.start);
  const end = minutesOf(quiet.end);
  if (start === undefined || end === undefined || start === end) return false;
  let parts;
  try {
    parts = zonedParts(now, quiet.tz || fallbackZone);
  } catch {
    parts = zonedParts(now, fallbackZone);
  }
  const current = parts.hour * 60 + parts.minute;
  return start < end ? current >= start && current < end : current >= start || current < end;
}

/** The owner's preferences allow a reminder right now. */
export function remindersAllowed(
  prefs: NotificationPreferences | undefined,
  now: Date,
  zone: string,
): boolean {
  if (!prefs || !prefs.enabled || !prefs.calendarReminders) return false;
  if (prefs.snoozedUntil) {
    const until = Date.parse(prefs.snoozedUntil);
    if (Number.isFinite(until) && until > now.getTime()) return false;
  }
  return !inQuietHours(prefs.quietHours, now, zone);
}

/* ------------------------------------------------------------- fired set */

/**
 * Reminders this browser has already raised, so a reload inside the fifteen
 * minutes a past trigger stays eligible does not raise it again. Shared by
 * every window of the browser, which also covers a leader handover.
 *
 * Storage can be missing or throw (private mode, blocked site data); then the
 * set lives in this page's memory instead, and the worst case is one repeated
 * notice after a reload. It has to live somewhere: the hooks re-plan every few
 * minutes, and a reminder is eligible for fifteen after its trigger, so an
 * empty set would raise the same notice again on every re-plan in between.
 */
export const FIRED_STORAGE_KEY = 'ols-mail.reminders-fired';
/** Longer than any reminder stays eligible, so pruning never re-arms one. */
const FIRED_RETENTION_MS = 2 * 24 * 60 * 60_000;

/** The fired set while storage is refusing; unused whenever it works. */
let firedInMemory: Record<string, number> = {};

function readFired(): Record<string, number> {
  try {
    const raw = localStorage.getItem(FIRED_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number') out[key] = value;
    }
    return out;
  } catch {
    return { ...firedInMemory };
  }
}

export function wasFired(key: string): boolean {
  return readFired()[key] !== undefined;
}

export function markFired(key: string, now: number): void {
  const fired = readFired();
  fired[key] = now;
  for (const [candidate, at] of Object.entries(fired)) {
    if (now - at > FIRED_RETENTION_MS) delete fired[candidate];
  }
  try {
    localStorage.setItem(FIRED_STORAGE_KEY, JSON.stringify(fired));
  } catch {
    // Without storage the set lives no longer than this page; see above.
    firedInMemory = fired;
  }
}
